import React from 'react';
import type { BoxProps } from '@withjoy/joykit';
import { Accordion, Box, Flex, styled, TextV2 } from '@withjoy/joykit';
import { ChevronDown, ChevronUp } from '@withjoy/joykit/icons';
import { animationTransition } from '@shared/utils/animationTransition';
import type { PaletteColorTokens } from '@withjoy/joykit/theme';

// --- Shared styled component for popover body scrollbar ---

export const PopoverBodyOverride = styled(Box)`
  ::-webkit-scrollbar {
    width: 8px;
  }
  ::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.mono5};
  }
`;

// --- Pill trigger (used by FilterPill and FormatFilterPill) ---

export const PillTrigger = React.forwardRef<
  HTMLButtonElement,
  {
    isOpen: boolean;
    activeFiltersCount: number;
    label: string;
    onClick?: () => void;
    color?: keyof PaletteColorTokens;
    backgroundColor?: (isOpen: boolean) => BoxProps['backgroundColor'];
  }
>(({ activeFiltersCount, isOpen, label, onClick, backgroundColor, color }, ref) => {
  const ArrowIcon = isOpen ? ChevronUp : ChevronDown;
  return (
    <Box
      backgroundColor={backgroundColor?.(isOpen) ?? (isOpen ? 'mono3' : 'mono2')}
      color={color ?? 'mono14'}
      as="button"
      height={40}
      paddingX={5}
      borderRadius={24}
      transition={animationTransition('background-color')}
      onClick={onClick}
      ref={ref}
    >
      <Box
        as="span"
        display="inline-flex"
        borderRadius="inherit"
        alignItems="center"
        columnGap={3}
        typographyVariant="button1"
        whiteSpace="nowrap"
      >
        {activeFiltersCount > 0 && (
          <Flex
            borderRadius="inherit"
            backgroundColor="black"
            color="white"
            size={30}
            justifyContent="center"
            alignItems="center"
          >
            {activeFiltersCount}
          </Flex>
        )}
        {label}
        <ArrowIcon size={16} />
      </Box>
    </Box>
  );
});

// --- Drawer accordion trigger (used by FiltersDrawer and FormatFilterAccordionItem) ---

const ActiveFilterCountTriggerButton = ({ children }: { children: React.ReactNode }) => {
  return (
    <Box
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      borderRadius="full"
      backgroundColor="#000"
      color="white"
      size={'inherit'}
    >
      {children}
    </Box>
  );
};

export const AccordionTrigger = ({ label, isExpanded, activeFilterCount, onClick }: { label: string; isExpanded: boolean; activeFilterCount: number; onClick: () => void }) => {
  const notExpandedButton = activeFilterCount > 0 ? <ActiveFilterCountTriggerButton>{activeFilterCount}</ActiveFilterCountTriggerButton> : <ChevronDown size={24} />;

  return (
    <Accordion.Button
      onClick={onClick}
      justifyContent="space-between"
      paddingLeft={6}
      paddingRight={6}
      height={56}
    >
      <TextV2>{label}</TextV2>
      <Box
        display="flex"
        size={30}
        justifyContent="center"
        alignItems="center"
      >
        {isExpanded ? <ChevronUp size={24} /> : notExpandedButton}
      </Box>
    </Accordion.Button>
  );
};
